import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import Brand from "@/components/Brand";
import { isVisible, validateFieldClient } from "@/components/IntakeTab";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public candidate intake form — reached via single-use magic link
 * /candidate/intake/:token. No auth required. Terminates the token
 * server-side on successful submit.
 */
export default function PublicIntakeForm() {
    const { token } = useParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [tpl, setTpl] = useState(null);
    const [answers, setAnswers] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await axios.get(`${API}/public/intake/${token}`);
            setTpl(r.data);
            setAnswers(r.data.already_answered || {});
        } catch (ex) {
            setError(ex.response?.data?.detail || "This link is invalid or has expired.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const requiredMissing = useMemo(() => {
        if (!tpl) return [];
        return (tpl.fields || [])
            .filter((f) => isVisible(f, answers))
            .filter((f) => f.required && !isFilled(f, answers))
            .map((f) => f.label);
    }, [tpl, answers]);
    // V1.5.6.4 — client-side validator errors so candidate sees a message
    // before hitting Submit. Server enforces the same rules.
    const fieldErrors = useMemo(() => {
        if (!tpl) return {};
        const errs = {};
        for (const f of (tpl.fields || [])) {
            if (!isVisible(f, answers)) continue;
            const e = validateFieldClient(f, answers[f.key]);
            if (e) errs[f.key] = e;
        }
        return errs;
    }, [tpl, answers]);
    const hasErrors = Object.keys(fieldErrors).length > 0;

    const submit = async () => {
        if (requiredMissing.length || hasErrors) return;
        setSubmitting(true);
        setError("");
        try {
            await axios.post(`${API}/public/intake/${token}`, { answers });
            setSubmitted(true);
        } catch (ex) {
            setError(ex.response?.data?.detail || "Submission failed. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center p-6">
                <p className="text-sm text-[#525252]">Loading intake form…</p>
            </div>
        );
    }

    if (error && !tpl) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center p-6">
                <div className="max-w-md w-full border-2 border-[#DC2626] bg-white p-6 text-center">
                    <Warning size={32} weight="fill" className="text-[#DC2626] mx-auto mb-3" />
                    <p className="text-lg font-black uppercase tracking-wider text-ink mb-2">
                        Link unavailable
                    </p>
                    <p className="text-sm text-[#525252]">{error}</p>
                    <p className="text-xs text-[#A3A3A3] mt-4">
                        Please ask your recruiter to send you a fresh link.
                    </p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="min-h-screen bg-paper flex items-center justify-center p-6">
                <div className="max-w-md w-full border-2 border-emerald-500 bg-white p-6 text-center">
                    <CheckCircle size={48} weight="fill" className="text-emerald-500 mx-auto mb-3" />
                    <p className="text-xl font-black uppercase tracking-wider text-ink mb-2">
                        Thank you!
                    </p>
                    <p className="text-sm text-[#525252]">
                        Your intake form has been submitted. Our team will get in touch shortly.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-paper p-4 sm:p-6" data-testid="public-intake-page">
            <div className="max-w-xl mx-auto">
                {/* Workspace-branded header — falls back to NaukriDaddy wordmark
                    if the tenant hasn't uploaded a custom logo. */}
                <div className="flex items-center justify-center mb-5 pt-2">
                    <Brand
                        variant="dark"
                        size="h-9"
                        logoUrl={tpl?.workspace?.logo_url || null}
                        name={tpl?.workspace?.name || "NaukriDaddy"}
                        to={null}
                        data-testid="public-intake-brand"
                    />
                </div>
                <div className="mb-5">
                    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-cobalt">
                        {tpl?.workspace?.name || "NaukriDaddy"} · Intake
                    </p>
                    <h1 className="text-2xl sm:text-3xl font-black text-ink mt-1">
                        Hi {tpl?.candidate?.name?.split(" ")[0] || "there"} 👋
                    </h1>
                    <p className="text-sm text-[#525252] mt-2">
                        Please fill this short form so we can share your profile with{" "}
                        <span className="font-bold text-ink">
                            {tpl?.job?.company || tpl?.job?.title || "our client"}
                        </span>. Takes ~2 minutes. Fields marked <span className="text-[#DC2626]">*</span> are required.
                    </p>
                </div>
                <div className="bg-white border-2 border-ink p-4 sm:p-6 space-y-4"
                     data-testid="public-intake-form">
                    {(tpl?.fields || []).filter((f) => isVisible(f, answers)).map((f) => (
                        <PublicFieldInput
                            key={f.key}
                            field={f}
                            value={answers[f.key]}
                            onChange={(v) => setAnswers((a) => ({ ...a, [f.key]: v }))}
                            error={fieldErrors[f.key]}
                        />
                    ))}
                    {error && (
                        <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-xs px-3 py-2">
                            {error}
                        </div>
                    )}
                    {requiredMissing.length > 0 && (
                        <p className="text-xs text-amber-700">
                            Please fill: {requiredMissing.join(", ")}
                        </p>
                    )}
                    <button
                        data-testid="public-intake-submit"
                        onClick={submit}
                        disabled={submitting || requiredMissing.length > 0 || hasErrors}
                        className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 disabled:cursor-not-allowed text-white py-3 text-sm font-black tracking-wider uppercase transition-colors"
                    >
                        {submitting ? "Submitting…" : "Submit intake form"}
                    </button>
                </div>
                <p className="text-[10px] text-[#A3A3A3] mt-4 text-center">
                    Your information is used only for this application. Link expires after submission.
                </p>
            </div>
        </div>
    );
}

function isFilled(f, answers) {
    const v = answers?.[f.key];
    if (v === null || v === undefined) return false;
    if (f.type === "text" && !String(v).trim()) return false;
    if (f.type === "duration_ym") {
        if (!v || typeof v !== "object") return false;
        return v.years != null || v.months != null;
    }
    if (typeof v === "string" && !v.trim()) return false;
    return true;
}

function PublicFieldInput({ field, value, onChange, error }) {
    const label = (
        <label className="block text-xs font-black uppercase tracking-wider text-[#525252] mb-1.5">
            {field.label}
            {field.unit && <span className="text-[#A3A3A3] normal-case ml-1">({field.unit})</span>}
            {field.required && <span className="text-[#DC2626] ml-1">*</span>}
        </label>
    );
    const commonClass = "w-full bg-white border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt";
    const errClass = error ? " border-[#DC2626] focus:border-[#DC2626]" : "";
    const inputClass = commonClass + errClass;
    const errNode = error ? (
        <p data-testid={`public-intake-error-${field.key}`}
           className="text-[11px] font-bold text-[#DC2626] mt-1">
            {error}
        </p>
    ) : null;

    if (field.type === "text") {
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <input
                    data-testid={`public-intake-input-${field.key}`}
                    type="text"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value)}
                    className={inputClass}
                />
            {errNode}
            </div>
        );
    }
    if (field.type === "number") {
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <input
                    data-testid={`public-intake-input-${field.key}`}
                    type="number"
                    step="0.01"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
                    className={inputClass}
                />
            {errNode}
            </div>
        );
    }
    if (field.type === "select") {
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <select
                    data-testid={`public-intake-input-${field.key}`}
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value || null)}
                    className={inputClass}
                >
                    <option value="">— Select —</option>
                    {(field.options || []).map((o) => (
                        <option key={o} value={o}>{o}</option>
                    ))}
                </select>
            {errNode}
            </div>
        );
    }
    if (field.type === "boolean") {
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <div className="grid grid-cols-2 gap-2">
                    {[
                        { v: true, l: "Yes" },
                        { v: false, l: "No" },
                    ].map((opt) => (
                        <button
                            key={opt.l}
                            type="button"
                            data-testid={`public-intake-input-${field.key}-${opt.l.toLowerCase()}`}
                            onClick={() => onChange(opt.v)}
                            className={`py-2.5 text-sm font-bold border transition-colors ${
                                value === opt.v
                                    ? "bg-cobalt text-white border-cobalt"
                                    : "bg-white border-[#E5E5E5] text-[#525252] hover:border-ink"
                            }`}
                        >
                            {opt.l}
                        </button>
                    ))}
                </div>
                {errNode}
            </div>
        );
    }
    if (field.type === "date") {
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <input
                    data-testid={`public-intake-input-${field.key}`}
                    type="date"
                    value={value ?? ""}
                    onChange={(e) => onChange(e.target.value || null)}
                    className={inputClass}
                />
            {errNode}
            </div>
        );
    }
    if (field.type === "duration_ym") {
        const v = value || {};
        return (
            <div data-testid={`public-intake-field-${field.key}`}>
                {label}
                <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center border border-[#E5E5E5] focus-within:border-cobalt bg-white">
                        <input
                            data-testid={`public-intake-input-${field.key}-years`}
                            type="number"
                            min="0"
                            placeholder="0"
                            value={v.years ?? ""}
                            onChange={(e) => onChange({ ...v, years: e.target.value === "" ? null : Number(e.target.value) })}
                            className="w-full px-3 py-2.5 text-sm focus:outline-none"
                        />
                        <span className="pr-3 text-xs text-[#A3A3A3]">yrs</span>
                    </div>
                    <div className="flex items-center border border-[#E5E5E5] focus-within:border-cobalt bg-white">
                        <input
                            data-testid={`public-intake-input-${field.key}-months`}
                            type="number"
                            min="0"
                            max="11"
                            placeholder="0"
                            value={v.months ?? ""}
                            onChange={(e) => onChange({ ...v, months: e.target.value === "" ? null : Number(e.target.value) })}
                            className="w-full px-3 py-2.5 text-sm focus:outline-none"
                        />
                        <span className="pr-3 text-xs text-[#A3A3A3]">mo</span>
                    </div>
                </div>
                {errNode}
            </div>
        );
    }
    return null;
}
